import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Link, useRouteMatch } from 'react-router-dom';
import { ConfirmationModal, DropdownMenuItem } from 'seagull';
import { CreditCard, DocumentCheckmark, Download, EmailSent, LinkIcon, MoneyReceive, Redo, Void } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { allowResendAction, allowUnmarkPaidExternallyAction, allowUnvoidAction, allowVoidAction } from '~/business-logic/invoiceLogic';
import { routes } from '~/constants';
import { useIoContext, useOverlayContext, useStore, useStoreActions } from '~/contexts';
import { useDownloadInvoicePdf } from '~/hooks/invoice/useDownloadInvoicePdf';
import { useCopyDirectLink } from '~/hooks/useCopyDirectLink';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { usePaidExternally } from '~/hooks/usePaidExternally';
import { useResendInvoice } from '~/hooks/useResendInvoice';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { invoiceSummaryModel } from '~/models';
import { useClientInvoiceActions } from '~/pages/ClientDetailPage/hooks/useClientInvoiceActions';
import { selectors } from '~/store/state-selectors';
const voidLoader = 'void';
export const GetInvoiceActionsMenuItemsTID = (parentTestId)=>getTestIds(parentTestId);
export function InvoiceActionsMenuItems(param) {
    let { invoice, customerId, testId, heapId, onRefreshOverride } = param;
    const { InvoiceActionsMenuItemsTestIds: TID } = useMemo(()=>GetInvoiceActionsMenuItemsTID(testId), [
        testId
    ]);
    const isInsideGlobalBillingHub = useRouteMatch(routes.globalBillingHub) != null;
    const { refresh, startUnvoidInvoice, startUnmarkPaidExternally } = useClientInvoiceActions(customerId);
    const onRefresh = onRefreshOverride !== null && onRefreshOverride !== void 0 ? onRefreshOverride : refresh;
    const onUnvoidInvoice = ()=>startUnvoidInvoice(invoice);
    const onUnmarkPaidExternally = ()=>startUnmarkPaidExternally(invoice);
    const { t: tableRowTexts } = useLocaleKeys('invoice.tableRow');
    const { t: commonTexts } = useLocaleKeys('common');
    const { t: invoiceTexts } = useLocaleKeys('invoice');
    const overlayContext = useOverlayContext();
    const { native } = useIoContext();
    const { markAsPaid, shouldShowMarkAsPaid } = usePaidExternally();
    const { resendInvoice } = useResendInvoice();
    const isVendor = useStore(selectors.user.isVendor);
    const email = useStore(selectors.user.email);
    const { api, logBI } = useStoreActions();
    const { copyDirectLink } = useCopyDirectLink();
    const { downloadInvoicePdf } = useDownloadInvoicePdf();
    const { AD_HOC_INVOICING } = useFeatureFlags();
    const isAdHoc = AD_HOC_INVOICING && invoice.isAdHoc;
    const heapAttr = (suffix)=>heapId ? {
            'data-heapid': "".concat(heapId, "-").concat(suffix)
        } : {};
    const displayStatus = invoiceSummaryModel.displayStatus(invoice);
    const paymentDetails = invoiceSummaryModel.paymentDetails(invoice);
    const relationshipId = invoiceSummaryModel.relationshipId(invoice);
    const agreementHref = isInsideGlobalBillingHub ? routes.globalBillingHubRoutes({
        clientId: invoice.relationship.customer.id,
        relationshipId
    }).agreement : routes.agreement(relationshipId);
    const onVoidInvoice = ()=>{
        overlayContext.showModal({
            biName: 'invoices_table_row_void_confirm',
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            openBottomSheetOnMobile: true,
            hideBottomSheetOnDismiss: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    bi: {
                        primaryButton: {
                            name: 'invoices_table_row_confirm'
                        },
                        secondaryButton: {
                            name: 'invoices_table_row_cancel'
                        }
                    },
                    title: tableRowTexts.voidTitle(),
                    content: tableRowTexts.voidConfirmation({
                        invoiceNumber: invoice.formattedNumber
                    }),
                    testId: TID.VoidConfirmationModal(),
                    primaryButtonLabel: commonTexts.buttons.imSure(),
                    secondaryButtonLabel: commonTexts.buttons.cancel(),
                    onSecondaryButtonClick: ()=>dismissModal(),
                    onPrimaryButtonClick: async ()=>{
                        try {
                            overlayContext.startLoader(voidLoader);
                            await api.invoice.void(invoice.id);
                            await onRefresh();
                            dismissModal(true);
                        } catch (error) {
                            overlayContext.showErrorToast(commonTexts.toasts.unableToVoidInvoice(), 'void-invoice-error');
                            dismissModal();
                        } finally{
                            overlayContext.stopLoader(voidLoader);
                        }
                    }
                });
            }
        });
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            shouldShowMarkAsPaid(displayStatus, paymentDetails, isVendor) && /*#__PURE__*/ _jsx(DropdownMenuItem, _object_spread_props(_object_spread({
                intent: Intent.InvoiceMarkPaidExternally,
                testId: TID.PaidExternally()
            }, heapAttr('paid-externally')), {
                iconStart: /*#__PURE__*/ _jsx(MoneyReceive, {}),
                onClick: ()=>markAsPaid(invoice, onRefresh),
                bi: {
                    name: 'mark_paid_externally'
                },
                children: commonTexts.buttons.markAsPaidExternally()
            })),
            allowResendAction(displayStatus, isVendor) && /*#__PURE__*/ _jsx(DropdownMenuItem, _object_spread_props(_object_spread({
                intent: Intent.InvoiceResend,
                testId: TID.ResendInvoice()
            }, heapAttr('resend-invoice')), {
                iconStart: /*#__PURE__*/ _jsx(EmailSent, {}),
                onClick: ()=>{
                    resendInvoice(invoice).then(onRefresh);
                },
                bi: {
                    name: 'resend_invoice'
                },
                children: commonTexts.buttons.resendInvoice()
            })),
            /*#__PURE__*/ _jsx(DropdownMenuItem, _object_spread_props(_object_spread({
                intent: Intent.InvoiceDownload,
                testId: TID.DownloadInvoice()
            }, heapAttr('download-invoice')), {
                iconStart: /*#__PURE__*/ _jsx(Download, {}),
                onClick: ()=>downloadInvoicePdf(invoice.id),
                disabled: !invoice.pdfExists,
                tooltipProps: invoice.pdfExists ? undefined : {
                    title: invoiceTexts.generatingPdf()
                },
                bi: {
                    name: 'download_invoice'
                },
                children: commonTexts.buttons.downloadInvoice()
            })),
            !native.isNative && /*#__PURE__*/ _jsx(DropdownMenuItem, _object_spread_props(_object_spread({
                testId: TID.Agreement()
            }, heapAttr('agreement')), {
                as: isAdHoc ? undefined : Link,
                to: isAdHoc ? undefined : agreementHref,
                disabled: isAdHoc,
                tooltipProps: isAdHoc ? {
                    title: invoiceTexts.adHocTooltip()
                } : undefined,
                iconStart: /*#__PURE__*/ _jsx(DocumentCheckmark, {}),
                bi: {
                    name: 'view_agreement'
                },
                children: commonTexts.buttons.viewAgreement()
            })),
            AD_HOC_INVOICING && !native.isNative && /*#__PURE__*/ _jsx(DropdownMenuItem, _object_spread_props(_object_spread({
                testId: TID.ManagePaymentMethods()
            }, heapAttr('manage-payment-methods')), {
                as: Link,
                to: routes.paymentMethods(relationshipId),
                iconStart: /*#__PURE__*/ _jsx(CreditCard, {}),
                bi: {
                    name: 'manage_payment_methods'
                },
                children: invoiceTexts.managePaymentMethods()
            })),
            allowVoidAction(displayStatus, paymentDetails, isVendor) && /*#__PURE__*/ _jsx(DropdownMenuItem, _object_spread_props(_object_spread({
                intent: Intent.InvoiceVoid,
                iconStart: /*#__PURE__*/ _jsx(Void, {}),
                onClick: onVoidInvoice,
                testId: TID.Void()
            }, heapAttr('void')), {
                bi: {
                    name: 'void_invoice'
                },
                children: commonTexts.buttons.voidInvoice()
            })),
            allowUnvoidAction(invoice, isVendor) && /*#__PURE__*/ _jsx(DropdownMenuItem, _object_spread_props(_object_spread({
                intent: Intent.InvoiceUnvoid,
                iconStart: /*#__PURE__*/ _jsx(Redo, {}),
                onClick: onUnvoidInvoice,
                testId: TID.Unvoid()
            }, heapAttr('unvoid')), {
                bi: {
                    name: 'unvoid_invoice'
                },
                children: commonTexts.buttons.unvoidInvoice()
            })),
            allowUnmarkPaidExternallyAction(invoice, isVendor) && /*#__PURE__*/ _jsx(DropdownMenuItem, _object_spread_props(_object_spread({
                intent: Intent.InvoiceUnmarkPaidExternally,
                iconStart: /*#__PURE__*/ _jsx(Redo, {}),
                onClick: onUnmarkPaidExternally,
                testId: TID.UnmarkPaidExternally()
            }, heapAttr('unmark-paid-externally')), {
                bi: {
                    name: 'unmark_paid_externally'
                },
                children: commonTexts.buttons.unmarkAsPaidExternally()
            })),
            /*#__PURE__*/ _jsx(DropdownMenuItem, _object_spread_props(_object_spread({
                intent: Intent.InvoiceCopyLink,
                testId: TID.CopyLink()
            }, heapAttr('copy-link')), {
                iconStart: /*#__PURE__*/ _jsx(LinkIcon, {}),
                bi: {
                    name: 'copy_link'
                },
                onClick: ()=>{
                    if (isVendor) {
                        logBI(14001, {
                            email
                        });
                    } else {
                        logBI(14003);
                    }
                    copyDirectLink(relationshipId, {
                        type: 'invoice',
                        id: invoice.id,
                        isAdHoc
                    });
                },
                children: commonTexts.buttons.copyLink()
            }))
        ]
    });
}
